import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import {
  type AddSystemRequest,
  type SystemWithCapacity,
  type UpdateSystemRequest
} from '@bliks-io/contracts';
import { ENTITY_ICONS } from '@/constants/entityIcons';
import { useMySystems } from '@/api/queries/useSystems';
import { useUserTombstoneLookup } from '@/api/queries/useOrgMembers';
import { useUpdateSystem } from '@/api/mutations/useSystems';
import { SystemFormDialog } from '../organization/systems/components/SystemFormDialog';
import { useAuthStore } from '@/stores/auth-store';
import { SystemTabsNav } from '@/components/systems/SystemTabsNav';
import {
  createSystemLogoColumn,
  createSystemNameColumn,
  createSystemOwnerColumn,
  createSystemCategoryColumn,
  createSystemLicenceSeatsColumn,
  createSystemActionsColumn
} from '../organization/systems/components/system-columns';
import {
  createEntityListPage,
  type EntityListPageDescriptor
} from '@/components/entity-list/EntityListPage';

export const mySystemsDescriptor: EntityListPageDescriptor<
  SystemWithCapacity,
  AddSystemRequest,
  UpdateSystemRequest,
  never
> = {
  documentTitleKey: 'pageTitles.mySystems',
  i18nPrefix: 'mySystems',
  icon: ENTITY_ICONS.system,
  TabsNav: SystemTabsNav,
  tableId: 'my-systems',
  deleteEntityPhraseKey: 'organization.systems.deletePhrase',
  useRows: () => useMySystems(),
  // Systems owned by the current user are edited here, never created or deleted.
  useMutations: () => ({ update: useUpdateSystem() }),
  useColumns: ({ rows, openEdit }) => {
    const { t } = useTranslation();
    const slug = useAuthStore().slug();
    const navigate = useNavigate();
    const userLookup = useUserTombstoneLookup();

    const handleView = useCallback(
      (system: SystemWithCapacity) => {
        navigate({ to: '/$slug/systems/$systemId', params: { slug, systemId: system._id } });
      },
      [navigate, slug]
    );

    const showLicenceSeats = rows.some((s) => s.licenceSeats !== undefined && s.licenceSeats > 0);

    return useMemo(() => {
      const cols = [
        createSystemLogoColumn(t),
        createSystemNameColumn(slug, t),
        createSystemCategoryColumn(t),
        createSystemOwnerColumn(t, userLookup)
      ];
      if (showLicenceSeats) {
        cols.push(createSystemLicenceSeatsColumn(t));
      }
      cols.push(
        createSystemActionsColumn(
          { canView: true, canEdit: true },
          { onView: handleView, onEdit: openEdit }
        )
      );
      return cols;
    }, [slug, t, handleView, openEdit, showLicenceSeats, userLookup]);
  },
  renderFormDialog: ({ open, onOpenChange, entity, onSubmit }) => (
    <SystemFormDialog
      open={open}
      onOpenChange={onOpenChange}
      onSubmit={async (data) => {
        await onSubmit(data);
      }}
      system={entity}
    />
  )
};

export const MySystemsListPage = createEntityListPage(mySystemsDescriptor);
